// Data service — HubSpot + Airtable + Business Central
import { fetchOperationsDeals } from '../hooks/useHubSpot';

const HUBSPOT_BASE_URL = '/hubspot-api/crm/v3/objects';
const HUBSPOT_TOKEN = process.env.REACT_APP_HUBSPOT_TOKEN;

const AIRTABLE_TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const AIRTABLE_BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID || process.env.REACT_APP_AIRTABLE_BASE_ID_DEV;
// In CRA builds only REACT_APP_* vars are exposed to browser code.
// Fallback to the active Airtable base so coach lookup keeps working
// even when a dedicated REACT_APP_AIRTABLE_BASE_ID_PROD is not set.
const AIRTABLE_BASE_ID_PROD = process.env.REACT_APP_AIRTABLE_BASE_ID_PROD || AIRTABLE_BASE_ID;
const AIRTABLE_PROJECTS_TABLE = 'tbl6KbHSbj2rTXBZJ';
const AIRTABLE_PROJECTS_TABLE_NAME = 'Projects';
const AIRTABLE_PROPERTIES_TABLE_NAME = 'Properties';
const AIRTABLE_TRANSACTIONS_TABLE_NAME = 'Transactions';
const AIRTABLE_TRANSACTIONS_UNIQUE_ID_FIELD = 'UNIQUEID (from Engagements)';
const AIRTABLE_TRANSACTIONS_COACH_FIELD = 'Coach';

const AIRTABLE_EXPORT_PROJECT_FIELDS = [
  'Project Name',
  'Project Unique ID',
  'Arras deadline',
  'Type',
  'Operation name',
  'Opportunity stage',
  'Completion status',
  'First Arras Payment',
  'First Arras Payment Date',
  'Second Arras Payment',
  'Second Arras Payment Date',
  'Third Arras Payment',
  'Third Arras Payment Date',
  'Fourth Arras Payment',
  'Fourth Arras Payment Date',
];

const AIRTABLE_EXPORT_PROPERTY_FIELDS = [
  'Address',
  'Type',
  'Date created',
  'Real settlement date',
  'Property UniqueID',
  'Project Unique ID',
  'Arras to be collected',
  'Senal to be collected',
  'Operation name',
];

const AIRTABLE_PROJECT_FIELDS = [
  'Project Name',
  'Type',
  'Operation name',
  'Project Unique ID',
  'Project status',
  'Arras amount',
  'Arras to collect',
  'Arras to be paid',
  'First Arras Payment',
  'Second Arras Payment',
  'Third Arras Payment',
  'Fourth Arras Payment',
  'First Arras Payment Date',
  'Second Arras Payment Date',
  'Third Arras Payment Date',
  'Fourth Arras Payment Date',
  'First Arras Payment Status',
  'Second Arras Payment Status',
  'Third Arras Payment Status',
  'Fourth Arras Payment Status',
  'Properties linked',
  'Country',
  'Project address',
];

export const fetchHubSpotData = async (endpoint, properties = [], limit = 100) => {
  const params = new URLSearchParams({ limit, properties: properties.join(',') });
  const response = await fetch(`${HUBSPOT_BASE_URL}/${endpoint}?${params}`, {
    headers: { Authorization: `Bearer ${HUBSPOT_TOKEN}` },
  });
  if (!response.ok) throw new Error(`HubSpot ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return data.results ?? [];
};

export const fetchAirtableProjects = async (maxRecords = 20) => {
  const fieldsParam = AIRTABLE_PROJECT_FIELDS.map(f => `fields[]=${encodeURIComponent(f)}`).join('&');
  const url = `/airtable-api/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_PROJECTS_TABLE}?maxRecords=${maxRecords}&${fieldsParam}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${AIRTABLE_TOKEN}` },
  });
  if (!response.ok) throw new Error(`Airtable ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return (data.records ?? []).map(mapAirtableProject);
};

const mapAirtableProject = (record) => {
  const f = record.fields;
  return {
    id: record.id,
    name: f['Project Name'] ?? f['Operation name'] ?? record.id,
    type: f['Type'] ?? 'PROJECT',
    uniqueId: f['Project Unique ID'] ?? null,
    status: f['Project status'] ?? null,
    arrasPageadas: f['Arras amount'] ?? 0,
    toBeCollected: f['Arras to collect'] ?? 0,
    collected: 0, // vendrá de BC
    address: f['Project address'] ?? null,
    country: f['Country'] ?? null,
    arrasPayments: buildArrasPayments(f),
    units: [], // se enriquecerán con Properties
  };
};

const buildArrasPayments = (f) => {
  const payments = [];
  const slots = [
    { amount: 'First Arras Payment', date: 'First Arras Payment Date', status: 'First Arras Payment Status', label: '1ª tanda' },
    { amount: 'Second Arras Payment', date: 'Second Arras Payment Date', status: 'Second Arras Payment Status', label: '2ª tanda' },
    { amount: 'Third Arras Payment', date: 'Third Arras Payment Date', status: 'Third Arras Payment Status', label: '3ª tanda' },
    { amount: 'Fourth Arras Payment', date: 'Fourth Arras Payment Date', status: 'Fourth Arras Payment Status', label: '4ª tanda' },
  ];
  for (const s of slots) {
    if (f[s.amount]) {
      payments.push({
        label: s.label,
        amount: f[s.amount],
        date: f[s.date] ?? null,
        status: f[s.status] ?? 'Prevista',
      });
    }
  }
  return payments;
};

export const fetchAirtableData = async () => {
  const projects = await fetchAirtableProjects(20);
  return { projects, properties: [], clients: [] };
};

const fetchAirtableRecords = async ({
  baseId = AIRTABLE_BASE_ID,
  table,
  fields = [],
  filterByFormula,
  pageSize = 100,
  cellFormat,
  userLocale,
  timeZone,
}) => {
  if (!baseId) {
    throw new Error('Airtable baseId is not configured');
  }
  const allRecords = [];
  let offset;

  do {
    const params = new URLSearchParams();
    params.set('pageSize', String(pageSize));
    if (offset) params.set('offset', offset);
    if (filterByFormula) params.set('filterByFormula', filterByFormula);
    if (cellFormat) params.set('cellFormat', cellFormat);
    if (userLocale) params.set('userLocale', userLocale);
    if (timeZone) params.set('timeZone', timeZone);
    for (const field of fields) params.append('fields[]', field);

    const url = `/airtable-api/v0/${baseId}/${encodeURIComponent(table)}?${params.toString()}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${AIRTABLE_TOKEN}` },
    });
    if (!response.ok) {
      throw new Error(`Airtable ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    allRecords.push(...(data.records ?? []));
    offset = data.offset;
  } while (offset);

  return allRecords;
};

const normalizeUniqueId = (value) => {
  if (value == null) return null;
  return String(value).trim();
};

const escapeAirtableFormulaValue = (value) => {
  return String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
};

const normalizeCoachName = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const fetchDistinctTransactionCoachesByUniqueId = async (uniqueId) => {
  const normalizedUniqueId = normalizeUniqueId(uniqueId);
  if (!normalizedUniqueId) {
    throw new Error('unique_id is required');
  }
  if (!AIRTABLE_BASE_ID_PROD) {
    throw new Error('Airtable base ID is not configured (set REACT_APP_AIRTABLE_BASE_ID[_PROD])');
  }

  const formulaUniqueId = escapeAirtableFormulaValue(normalizedUniqueId);
  const records = await fetchAirtableRecords({
    baseId: AIRTABLE_BASE_ID_PROD,
    table: AIRTABLE_TRANSACTIONS_TABLE_NAME,
    fields: [AIRTABLE_TRANSACTIONS_COACH_FIELD],
    filterByFormula: `{${AIRTABLE_TRANSACTIONS_UNIQUE_ID_FIELD}}='${formulaUniqueId}'`,
    cellFormat: 'string',
    userLocale: 'es',
    timeZone: 'Europe/Madrid',
  });

  const uniqueCoaches = new Set();
  for (const record of records) {
    const coachValue = record.fields?.[AIRTABLE_TRANSACTIONS_COACH_FIELD];
    if (Array.isArray(coachValue)) {
      for (const coach of coachValue) {
        const normalizedCoach = normalizeCoachName(coach);
        if (normalizedCoach) uniqueCoaches.add(normalizedCoach);
      }
      continue;
    }

    const normalizedCoach = normalizeCoachName(coachValue);
    if (normalizedCoach) uniqueCoaches.add(normalizedCoach);
  }

  return Array.from(uniqueCoaches);
};

const toExportProject = (record) => {
  const f = record.fields ?? {};
  return {
    'Project Name': f['Project Name'] ?? null,
    'Project Unique ID': f['Project Unique ID'] ?? null,
    'Arras deadline': f['Arras deadline'] ?? null,
    Type: f['Type'] ?? null,
    'Operation name': f['Operation name'] ?? null,
    'Opportunity stage': f['Opportunity stage'] ?? null,
    'Completion status': f['Completion status'] ?? null,
    'First Arras Payment': f['First Arras Payment'] ?? null,
    'First Arras Payment Date': f['First Arras Payment Date'] ?? null,
    'Second Arras Payment': f['Second Arras Payment'] ?? null,
    'Second Arras Payment Date': f['Second Arras Payment Date'] ?? null,
    'Third Arras Payment': f['Third Arras Payment'] ?? null,
    'Third Arras Payment Date': f['Third Arras Payment Date'] ?? null,
    'Fourth Arras Payment': f['Fourth Arras Payment'] ?? null,
    'Fourth Arras Payment Date': f['Fourth Arras Payment Date'] ?? null,
  };
};

const toExportProperty = (record) => {
  const f = record.fields ?? {};
  return {
    Address: f['Address'] ?? null,
    Type: f['Type'] ?? null,
    'Date created': f['Date created'] ?? null,
    'Real settlement date': f['Real settlement date'] ?? null,
    'Property UniqueID': f['Property UniqueID'] ?? null,
    'Project Unique ID': f['Project Unique ID'] ?? null,
    'Arras to be collected': f['Arras to be collected'] ?? null,
    'Senal to be collected': f['Senal to be collected'] ?? null,
    'Operation name': f['Operation name'] ?? null,
  };
};

export const exportProjectsAndPropertiesJson = async ({
  createdFrom = '2026-01-01',
} = {}) => {
  const createdFromDate = createdFrom.trim();
  const dayBefore = new Date(`${createdFromDate}T00:00:00.000Z`);
  dayBefore.setUTCDate(dayBefore.getUTCDate() - 1);
  const formulaDate = dayBefore.toISOString().slice(0, 10);

  // Filtro por fecha de creación del registro en Airtable (CREATED_TIME)
  const projects = await fetchAirtableRecords({
    table: AIRTABLE_PROJECTS_TABLE_NAME,
    fields: AIRTABLE_EXPORT_PROJECT_FIELDS,
    filterByFormula: `IS_AFTER(CREATED_TIME(), '${formulaDate}')`,
  });

  const properties = await fetchAirtableRecords({
    table: AIRTABLE_PROPERTIES_TABLE_NAME,
    fields: AIRTABLE_EXPORT_PROPERTY_FIELDS,
  });

  const projectRows = projects.map(toExportProject);
  const propertyRows = properties.map(toExportProperty);

  const propertiesByProjectUniqueId = {};
  for (const property of propertyRows) {
    const key = normalizeUniqueId(property['Project Unique ID']);
    if (!key) continue;
    if (!propertiesByProjectUniqueId[key]) propertiesByProjectUniqueId[key] = [];
    propertiesByProjectUniqueId[key].push(property);
  }

  const joined = projectRows.map(project => {
    const key = normalizeUniqueId(project['Project Unique ID']);
    return {
      project,
      properties: key ? (propertiesByProjectUniqueId[key] ?? []) : [],
    };
  });

  return {
    generatedAt: new Date().toISOString(),
    filters: {
      projectsCreatedFrom: createdFromDate,
    },
    totals: {
      projects: projectRows.length,
      properties: propertyRows.length,
      joined: joined.length,
    },
    projects: projectRows,
    properties: propertyRows,
    joined,
  };
};

// Enriquece los proyectos de Airtable con los deals de HubSpot Operations
// Matching: uniqueId de Airtable vs uniqueId parseado del dealname de HubSpot
export const enrichProjectsWithHubSpot = async (airtableProjects, { maxPages = 20 } = {}) => {
  const hubspotDeals = await fetchOperationsDeals({ maxPages });

  // Índice por uniqueId para lookup O(1)
  const dealsByUniqueId = {};
  for (const deal of hubspotDeals) {
    if (deal.uniqueId) {
      dealsByUniqueId[deal.uniqueId] = deal;
    }
  }

  // Enriquecer units de cada proyecto con datos de HubSpot
  return airtableProjects.map(project => ({
    ...project,
    units: (project.units ?? []).map(unit => {
      const hsData = unit.uniqueId ? dealsByUniqueId[unit.uniqueId] : null;
      if (!hsData) return unit;
      return {
        ...unit,
        client: hsData.client ?? unit.client,
        address: hsData.address ?? unit.address,
        toBeCollected: hsData.toBeCollected || unit.toBeCollected,
        collected: hsData.collected || unit.collected,
        diferencia: hsData.diferencia,
        señal: hsData.señal,
        status: hsData.status,
        arrasStatus: hsData.arrasStatus,
        signingDate: hsData.signingDate,
        hubspotUrl: hsData.hubspotUrl,
      };
    }),
    // Si el proyecto no tiene units pero hay deals que le pertenecen, los añadimos como units
    _hubspotDealsCount: hubspotDeals.filter(d => 
      project.uniqueId && d.uniqueId && d.uniqueId.startsWith(project.uniqueId?.slice(0, 6))
    ).length,
  }));
};

// Devuelve los deals de HubSpot Operations sin asociar a ningún proyecto de Airtable
export const fetchUnmatchedHubSpotDeals = async ({ maxPages = 20 } = {}) => {
  return fetchOperationsDeals({ maxPages });
};

export const fetchBCData = async () => {
  return { transactions: [] };
};

/**
 * Builds lookup maps from a BC GL entries JSON.
 *
 * Join rules (per spec):
 *
 *   Importe (DL) < 0  → Cobro de cliente
 *     Join key: entry['Unique ID'] (BC) === unit.clientUniqueId (SP-XXX from HubSpot)
 *     → collectedByClientUniqueId
 *
 *   Importe (DL) > 0  AND description does NOT contain "refund"
 *     → Arras pagadas por Prophero al vendedor
 *     Join key: entry['Project Unique ID'] (BC) === project.uniqueId (PROJ… from Airtable)
 *     → arrasPageadasByProjectUniqueId
 *
 *   Importe (DL) > 0  AND description contains "refund" (case-insensitive)
 *     → Arras to be refund pagadas (devoluciones al cliente ya ejecutadas)
 *     Join key: entry['Project Unique ID'] (BC) === project.uniqueId
 *     These are added to arrasPageadas AND stored separately in refundEntriesByProjectUniqueId
 *     → refundEntriesByProjectUniqueId / refundTotalByProjectUniqueId
 */
export const buildBCLookups = (bcJson) => {
  const arrasPageadasByProjectUniqueId = {};
  const arrasEntriesByProjectUniqueId = {};
  const refundEntriesByProjectUniqueId = {};
  const refundTotalByProjectUniqueId = {};
  const collectedByClientUniqueId = {};
  // Latest "Fecha de registro" for each client Unique ID (cobros negativos)
  const collectedDateByClientUniqueId = {};

  for (const entry of bcJson?.entries ?? []) {
    const importe = entry['Importe (DL)'] ?? 0;

    if (importe > 0) {
      const projUid = entry['Project Unique ID']?.trim();
      const desc = entry['Description'] ?? '';
      const isRefund = desc.toLowerCase().includes('refund');

      if (isRefund) {
        // Arras to be refund pagadas — se suma a arrasPageadas y se guarda en desglose.refunds
        if (projUid) {
          refundTotalByProjectUniqueId[projUid] =
            (refundTotalByProjectUniqueId[projUid] ?? 0) + importe;
          if (!refundEntriesByProjectUniqueId[projUid]) refundEntriesByProjectUniqueId[projUid] = [];
          refundEntriesByProjectUniqueId[projUid].push({
            date: entry['Fecha de registro'] ?? null,
            amount: -importe,
            desc,
            ref: entry['No documento'] || null,
            status: 'Pagado',
          });
        }
      } else if (projUid) {
        arrasPageadasByProjectUniqueId[projUid] =
          (arrasPageadasByProjectUniqueId[projUid] ?? 0) + importe;
        if (!arrasEntriesByProjectUniqueId[projUid]) arrasEntriesByProjectUniqueId[projUid] = [];
        arrasEntriesByProjectUniqueId[projUid].push({
          date: entry['Fecha de registro'] ?? null,
          amount: importe,
          desc,
          ref: entry['No documento'] ?? null,
          status: 'Pagado',
        });
      }
    } else if (importe < 0) {
      const clientUid = entry['Unique ID']?.trim();
      if (clientUid) {
        collectedByClientUniqueId[clientUid] =
          (collectedByClientUniqueId[clientUid] ?? 0) + Math.abs(importe);
        // Keep the most recent registration date
        const entryDate = entry['Fecha de registro'] ?? null;
        if (entryDate) {
          const existing = collectedDateByClientUniqueId[clientUid];
          if (!existing || entryDate > existing) {
            collectedDateByClientUniqueId[clientUid] = entryDate;
          }
        }
      }
    }
  }

  return {
    arrasPageadasByProjectUniqueId,
    arrasEntriesByProjectUniqueId,
    refundEntriesByProjectUniqueId,
    refundTotalByProjectUniqueId,
    collectedByClientUniqueId,
    collectedDateByClientUniqueId,
  };
};

/**
 * Enriches projects with BC data.
 *
 * Project level (arrasPageadas):
 *   BC entries with Importe > 0 are joined by entry['Project Unique ID'] === project.uniqueId.
 *   Sum of all matching positive entries → project.arrasPageadas.
 *
 * Unit level (collected):
 *   BC entries with Importe < 0 are joined by entry['Unique ID'] === unit.clientUniqueId
 *   (the SP-XXX ID from HubSpot, stored after enrichUnitsWithHubSpot runs).
 *   Sum of abs values → unit.collected.
 *
 * Note: clientUniqueId is populated by enrichUnitsWithHubSpot. If BC enrichment runs
 * before HubSpot, unit.collected will be 0 and gets overwritten once HubSpot data arrives.
 */
export const enrichProjectsWithBC = (projects, bcJson) => {
  const {
    arrasPageadasByProjectUniqueId,
    arrasEntriesByProjectUniqueId,
    refundEntriesByProjectUniqueId,
    refundTotalByProjectUniqueId,
    collectedByClientUniqueId,
    collectedDateByClientUniqueId,
  } = buildBCLookups(bcJson);

  return projects.map(project => {
    const bcArras = project.uniqueId
      ? (arrasPageadasByProjectUniqueId[project.uniqueId] ?? 0)
      : 0;
    const bcRefundTotal = project.uniqueId
      ? (refundTotalByProjectUniqueId[project.uniqueId] ?? 0)
      : 0;
    // arrasPageadas = arras pagadas al vendedor + refunds ya ejecutados al cliente
    const arrasPageadas = (bcArras + bcRefundTotal) || project.arrasPageadas;

    // Si el desglose.reserva viene vacío de Airtable (no hay tandas registradas),
    // lo construimos desde los asientos BC con Importe > 0 (excluye refunds).
    const bcEntries = project.uniqueId ? (arrasEntriesByProjectUniqueId[project.uniqueId] ?? []) : [];
    const existingReserva = project.desglose?.reserva ?? [];
    const bcRefundEntries = project.uniqueId ? (refundEntriesByProjectUniqueId[project.uniqueId] ?? []) : [];
    const existingRefunds = project.desglose?.refunds ?? [];
    const desglose = {
      ...project.desglose,
      reserva: existingReserva.length > 0 ? existingReserva : bcEntries,
      // BC refund entries replace any existing mock refunds when present
      refunds: bcRefundEntries.length > 0 ? bcRefundEntries : existingRefunds,
    };

    const enrichedUnits = project.units.map(unit => {
      // clientUniqueId is the SP-XXX ID from HubSpot (set after HubSpot enrichment).
      // Falls back to unit.uniqueId for compatibility when both fields happen to match.
      const lookupKey = unit.clientUniqueId ?? unit.uniqueId;
      const bcCollected = lookupKey ? (collectedByClientUniqueId[lookupKey] ?? 0) : 0;
      const collected = bcCollected || unit.collected;
      const bcCollectedDate = lookupKey ? (collectedDateByClientUniqueId[lookupKey] ?? null) : null;
      const collectedDate = bcCollectedDate || unit.collectedDate || null;

      let status = unit.status;
      if (unit.client) {
        status = unit.toBeCollected > 0 && collected >= unit.toBeCollected ? 'Pagado' : 'Parcial';
      } else if (collected > 0) {
        status = 'Parcial';
      }

      return { ...unit, collected, collectedDate, status };
    });

    const collected = enrichedUnits.reduce((s, u) => s + (u.collected ?? 0), 0);

    return {
      ...project,
      arrasPageadas,
      collected,
      desglose,
      units: enrichedUnits,
      soldUnits: enrichedUnits.filter(u => u.client != null).length,
    };
  });
};

export const runMatching = async (airtableData, bcData) => {
  return [];
};

// ─── JSON export mapper ───────────────────────────────────────────────────────

const PAYMENT_SLOTS = [
  { amount: 'First Arras Payment',  date: 'First Arras Payment Date',  label: '1ª tanda' },
  { amount: 'Second Arras Payment', date: 'Second Arras Payment Date', label: '2ª tanda' },
  { amount: 'Third Arras Payment',  date: 'Third Arras Payment Date',  label: '3ª tanda' },
  { amount: 'Fourth Arras Payment', date: 'Fourth Arras Payment Date', label: '4ª tanda' },
];

const buildTimelineFromJson = (p) => {
  const result = [];
  for (const slot of PAYMENT_SLOTS) {
    if (p[slot.amount] != null) {
      result.push({
        label: slot.label,
        amount: p[slot.amount],
        date: p[slot.date] ?? null,
        status: 'Pagado',
      });
    }
  }
  return result;
};

export const mapJsonProject = (p) => {
  const timeline = buildTimelineFromJson(p);
  // arrasPageadas vendrá de Business Central — se deja a 0 hasta la integración
  const arrasPageadas = 0;
  const properties = (p.properties ?? []).map((prop, i) => {
    const toBeCollected = prop['Arras to be collected'] ?? 0;
    const señal = prop['Senal to be collected'] ?? 0;
    const settlementDates = prop['Real settlement date'];
    const firmaEst = Array.isArray(settlementDates)
      ? settlementDates[0] ?? null
      : settlementDates ?? null;
    return {
      id: prop['Property UniqueID'] ?? `prop-${i}`,
      name: prop['Address'] ?? `Unidad ${i + 1}`,
      address: prop['Address'] ?? null,
      uniqueId: prop['Property UniqueID'] ?? null,
      client: null,
      toBeCollected,
      señal,
      collected: 0,
      firmaEst,
      status: 'Sin cliente',
    };
  });

  const toBeCollected = properties.reduce((s, u) => s + (u.toBeCollected ?? 0), 0);

  return {
    id: p['Project Unique ID'] ?? p['Operation name'] ?? Math.random().toString(36).slice(2),
    name: p['Project Name'] ?? p['Operation name'] ?? '—',
    operationName: p['Operation name'] ?? null,
    uniqueId: p['Project Unique ID'] ?? null,
    type: p['Type'] ?? 'PROJECT',
    arrasPageadas,
    toBeCollected,
    collected: 0,
    refundPending: 0,
    timeline,
    desglose: { reserva: timeline.map(t => ({ date: t.date, amount: t.amount, desc: t.label })), refunds: [] },
    units: properties,
    totalUnits: properties.length,
    soldUnits: 0,
  };
};

const PINNED_PROJECT_IDS = ['PROJ0522-SIO-0006', 'PROJ0510-DXN-0018', 'PROJ0515-3WJ-0004'];

export const loadProjectsFromJson = (jsonData) => {
  const projects = jsonData?.projects ?? jsonData ?? [];
  const mapped = projects.map(mapJsonProject);

  const pinned = PINNED_PROJECT_IDS
    .map(id => mapped.find(p => p.uniqueId === id))
    .filter(Boolean);
  const rest = mapped.filter(p => !PINNED_PROJECT_IDS.includes(p.uniqueId));

  return [...pinned, ...rest];
};

// Enriquece las units de los proyectos con datos de HubSpot.
// Join: unit.uniqueId (Property UniqueID, ej: "SP11022861") === deal.propertyId
export const enrichUnitsWithHubSpot = (projects, hubspotDeals) => {
  const dealsByPropertyId = new Map();
  for (const deal of hubspotDeals) {
    if (deal.propertyId) dealsByPropertyId.set(deal.propertyId, deal);
  }

  return projects.map(project => {
    const enrichedUnits = project.units.map(unit => {
      const deal = unit.uniqueId ? dealsByPropertyId.get(unit.uniqueId) : null;
      if (!deal) return unit;
      const client = deal.client ?? unit.client;
      const collected = unit.collected ?? 0;
      const toBeCollected = unit.toBeCollected ?? 0;
      let status = 'Sin cliente';
      if (client) {
        status = toBeCollected > 0 && collected >= toBeCollected ? 'Pagado' : 'Parcial';
      }
      return {
        ...unit,
        client,
        hsUniqueId: deal.propertyId ?? unit.uniqueId,
        clientUniqueId: deal.uniqueId ?? null,
        status,
      };
    });
    const collected = enrichedUnits.reduce((s, u) => s + (u.collected ?? 0), 0);
    return {
      ...project,
      collected,
      units: enrichedUnits,
      soldUnits: enrichedUnits.filter(u => u.client != null).length,
    };
  });
};

export const calculateNetPosition = (project) => {
  return project.arrasPageadas - project.collected;
};

export const getProjectStatus = (project) => {
  const totalUnits = project.units.length;
  const paidUnits = project.units.filter(u => u.status === 'Pagado').length;
  return `${paidUnits}/${totalUnits}`;
};
